/**
 * Copyright © 2026 Rémi Pace.
 * This file is part of Abc-Map.
 *
 * Abc-Map is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of
 * the License, or (at your option) any later version.
 *
 * Abc-Map is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General
 * Public License along with Abc-Map. If not, see <https://www.gnu.org/licenses/>.
 */

import type { ReaderImplementation } from './ReaderImplementation';
import type { AbcFile, AbcProjection, VectorMetadata } from '@abc-map/shared';
import { BlobIO, LayerType } from '@abc-map/shared';
import { FileFormat, FileFormats } from '../FileFormats';
import { TopoJSON } from 'ol/format';
import VectorSource from 'ol/source/Vector';
import uuid from 'uuid-random';
import type { LayerWrapper } from '../../geo/layers/LayerWrapper';
import { LayerFactory } from '../../geo/layers/LayerFactory';
import type { ReadResult } from '../ReadResult';
import { ReadStatus } from '../ReadResult';
import { normalizeFeatures } from './normalizeFeatures';
import { prefixedTranslation } from '../../../i18n/i18n';

const t = prefixedTranslation('DataReader');

export class TopoJsonReader implements ReaderImplementation {
  public async isSupported(files: AbcFile<Blob>[]): Promise<boolean> {
    return files.filter((f) => FileFormats.fromPath(f.path) === FileFormat.TOPOJSON).length > 0;
  }

  public async read(files: AbcFile<Blob>[], targetProjection: AbcProjection): Promise<ReadResult> {
    const layers: LayerWrapper[] = [];
    const format = new TopoJSON();
    const _files = files.filter((f) => FileFormats.fromPath(f.path) === FileFormat.TOPOJSON);
    for (const file of _files) {
      const content = await BlobIO.asString(file.content);
      let features = format.readFeatures(content, { featureProjection: targetProjection.name, dataProjection: 'EPSG:4326' });
      features = normalizeFeatures(features);

      const layer = LayerFactory.newVectorLayer(new VectorSource({ features }));
      const metadata: VectorMetadata = {
        id: uuid(),
        name: t('TopoJSON_import'),
        type: LayerType.Vector,
        active: false,
        opacity: 1,
        visible: true,
      };
      layer.setMetadata(metadata);

      layers.push(layer);
    }

    return { status: ReadStatus.Succeed, layers };
  }
}
